import {Action, getModule, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import store from '@/store';
import {find, pick, filter, remove, findIndex, map} from 'lodash';
import {Vue} from 'vue-property-decorator';
import {MenuStore} from '@/store/modules/menu.store';

export interface ICartStoreState {
	items: ICartItem[];
}

export interface ICartItem {
	lineItemId?: number;
	cartItemId?: string;
	ticketId: string;
	courseId: number;
	itemId: string;
	quantity: number;
	options: ICartItemOption[];
	specialRequests: string;
	menuId: string;
	total?: number;
	status?: 'PRE_ORDERED' | 'ORDERED' | 'CLAIMED' | 'PREPARED' | 'STAGED' | 'DELIVERED' | 'CANCELLED';
}

export interface ICartItemOption {
	optionId: string;
	choices: ICartItemOptionChoice[];
}

export interface ICartItemOptionChoice {
	choiceId: string;
	quantity: number;
}

export interface ICartMergedItem {
	lineItemId?: number;
	cartItemId?: string;
	ticketId: string;
	courseId: number;
	itemId: string;
	menuId: string;
	name: string;
	description: string;
	price: number;
	total?: number;
	quantity: number;
	options: ICartMergedItemOption[];
	specialRequests: string;
	status?: 'PRE_ORDERED' | 'ORDERED' | 'CLAIMED' | 'PREPARED' | 'STAGED' | 'DELIVERED' | 'CANCELLED';
}

export interface ICartMergedItemOption {
	optionId: string;
	name: string;
	price: number;
	exclusive: boolean;
	required: boolean;
	freeChoicesCount: number;
	priority: number;
	choices: ICartMergedItemOptionChoice[];
}

export interface ICartMergedItemOptionChoice {
	choiceId: string;
	name: string;
	description: string;
	price: number;
	quantity: number;
}

@Module({dynamic: true, store, name: 'cart', namespaced: true})
class Cart extends VuexModule implements ICartStoreState {
	items: ICartItem[] = [];

	public static mergeAndAdd(existingItems: any[], newItems: any[], predicateKeys: string[]) {
		newItems.forEach((newItem) => {
			const existingItemIndex = findIndex(existingItems, pick(newItem, predicateKeys));
			if(existingItemIndex > -1) {
				Vue.set(existingItems, existingItemIndex, Object.assign({}, existingItems[existingItemIndex], newItem));
			} else {
				existingItems.push(newItem);
			}
		});
	}

	get byId() {
		return (cartItemId: string): ICartItem | null => {
			return find(this.items, {cartItemId: cartItemId}) || null;
		};
	}

	get itemsByTicket() {
		return (ticketId: string): ICartItem[] => {
			return filter(this.items, {ticketId: ticketId});
		};
	}

	get mergedItemsByTicket() {
		return (ticketId: string): ICartMergedItem[] => {
			return this.mergeItems(filter(this.items, {ticketId: ticketId}));
		};
	}

	get mergeItems() {
		return (items: ICartItem[]): ICartMergedItem[] => {
			return items.map((item) => {
				const fullItem = MenuStore.itemByMenu(item.menuId, item.itemId);
				if(!fullItem) {
					throw new Error('Item missing from menu');
				}
				return {
					cartItemId: item.cartItemId,
					lineItemId: item.lineItemId,
					ticketId: item.ticketId,
					courseId: item.courseId,
					itemId: item.itemId,
					menuId: item.menuId,
					name: fullItem.name,
					description: fullItem.description,
					price: fullItem.price,
					total: item.total,
					quantity: item.quantity,
					specialRequests: item.specialRequests,
					status: item.status,
					options: item.options.map((option) => {
						const fullOption = MenuStore.optionByMenu(item.menuId, option.optionId);
						if(!fullOption) {
							throw new Error('Option missing from menu');
						}
						return {
							optionId: option.optionId,
							name: fullOption.name,
							price: fullOption.price,
							exclusive: fullOption.exclusive,
							required: fullOption.required,
							freeChoicesCount: fullOption.freeChoicesCount,
							priority: fullOption.priority,
							choices: option.choices.map((choice) => {
								const fullChoice = find(fullOption.choices, {choiceId: choice.choiceId});
								if(!fullChoice) {
									throw new Error('Choice missing from option');
								}
								return {
									choiceId: choice.choiceId,
									name: fullChoice.name,
									description: fullChoice.description,
									price: fullChoice.price,
									quantity: choice.quantity,
								};
							}),
						};
					}),
				};
			});
		};
	}

	@Action
	public addOrUpdateItem(item: ICartItem) {
		this.ADD_OR_UPDATE_ITEM(item);
	}

	@Action
	public removeItem(cartItemId: string) {
		this.REMOVE_ITEM(cartItemId);
	}

	@Action
	public clearCart(ticketId: string) {
		this.CLEAR_CART(ticketId);
	}

	@Mutation
	public ADD_OR_UPDATE_ITEM(item: ICartItem) {
		Cart.mergeAndAdd(this.items, [item], ['cartItemId']);
	}

	@Mutation
	public REMOVE_ITEM(cartItemId: string) {
		const foundIndex = findIndex(this.items, {cartItemId: cartItemId});
		if(foundIndex !== -1) {
			this.items.splice(foundIndex, 1);
		}
	}

	@Mutation
	public CLEAR_CART(ticketId: string) {
		let foundIndex = findIndex(this.items, {ticketId: ticketId});
		while(foundIndex !== -1) {
			this.items.splice(foundIndex, 1);
			foundIndex = findIndex(this.items, {ticketId: ticketId});
		}
		// this.items = [];
	}
}

export const CartStore = getModule(Cart);
